import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import MethodAction from '../actions/MethodAction'
import { push } from '@lagunovsky/redux-react-router'
import {
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_METHOD,
    PATH_REFERENCIAL_METHOD_NEW,
} from '../../../../home/constants/RouteConstants'
import { sandreMethodDefaultLink } from '../../../../conf/SieauConstants'
import ReferencialAction from '../../../action/ReferencialAction'
import HomeAction from '../../../../home/actions/HomeAction'
import { getReferencialData, reloadJobSync } from '../../../util/ReferencialUtils'
import logoSandre from 'assets/pictures/logo_sandre.png'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import {
    nbPerPageLabel,
    REFERENCIAL_TYPE_NAME,
    REFERENTIAL_JOBSYNC_ID,
} from 'referencial/constants/ReferencialConstants'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import { Button, Card, CardContent, Grid2, Stack } from '@mui/material'
import useSessionStorage from 'utils/customHook/useSessionStorage'
import { hasValue } from 'utils/NumberUtil'
import { searchAllCharacters } from 'utils/StringUtil'
import { CardTitle } from '../../../../components/card/NewCard'
import { CardTable } from '../../../../components/datatable/NewTable'
import JobSyncStatus from '../../JobSyncStatus'

const headers = ['code', 'name', 'status']

const MethodsApp = () => {
    const {
        methods,
        referencialStatus,
    } = useSelector(store => ({
        methods: store.MethodReducer.methods,
        referencialStatus: store.ReferencialReducer.referencialStatus,
    }), shallowEqual)

    const [filter, setFilter] = useSessionStorage(`referencial_${REFERENCIAL_TYPE_NAME.method}`, {})
    const [filterTmp, setFilterTmp] = useState(filter)
    const [jobSync, setJobSync] = useState(undefined)

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.method, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.method, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(MethodAction.fetchMethods())
        dispatch(HomeAction.setHelpLink('', ''))
        if (!referencialStatus.length) {
            dispatch(ReferencialAction.fetchReferencialStatus())
        }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: PATH_REFERENCIAL,
    }, {
        title: i18n.methods,
        href: PATH_REFERENCIAL_METHOD,
    }], [])

    const methodByStatus = useMemo(() => hasValue(filter.status) ? methods.filter(h => parseInt(h.status) === filter.status) : methods, [filter.status, methods])
    const referencialData = useMemo(() => getReferencialData(methodByStatus), [methodByStatus])
    const data = useMemo(() => {
        return referencialData
            .filter((obj) => searchAllCharacters(headers.map((key) => obj[key]).join())
                .includes(searchAllCharacters(filter.searchValue || '')))
    }, [referencialData, filter])

    useActions(() => {
        return {
            new: () => dispatch(push(PATH_REFERENCIAL_METHOD_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(data, headers),
                        titleFile: i18n.methodList,
                    },
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.method, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.method, setJobSync))),
                        withInsertAllOption: true,
                    },
                    purge: {
                        onPurge: () => dispatch(MethodAction.purgeMethod()),
                    },
                },
            },
            links: [{
                href: sandreMethodDefaultLink,
                img: logoSandre,
                label: i18n.sandre.toUpperCase(),
            }],
        }
    }, [data])

    const colorData = useMemo(() => data.map(p => ({
        id: p.id,
        code: { value: p.code },
        name: { value: p.name },
        status: { value: p.status },
        colorLine: p.active === '1' ? undefined : '#c5c5c5',
    })).sort((a, b) => parseInt(a.code.value) - parseInt(b.code.value)), [data])

    return (
        <Stack spacing={1} margin={1}>
            <Card>
                <CardTitle title={i18n.criterias} color='bannerColor'/>
                <CardContent>
                    <Grid2 container spacing={1}>
                        <Grid2 size={3}>
                            <Input
                                title={i18n.search}
                                value={filterTmp.searchValue}
                                onChange={v => setFilterTmp({ ...filterTmp, searchValue: v })}
                                onEnterKeyPress={() => setFilter({ ...filterTmp })}
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <Select
                                options={referencialStatus}
                                label={i18n.status}
                                onChange={v => setFilterTmp({ ...filterTmp, status: v })}
                                value={filterTmp.status}
                                nullLabel='&nbsp;'
                            />
                        </Grid2>
                        <Grid2 size='grow'>
                            <JobSyncStatus jobSync={jobSync}/>
                        </Grid2>
                        <Grid2 size={1} marginTop={2}>
                            <Button className='right' onClick={() => setFilter({ ...filterTmp })} variant='contained' color='primary'>
                                {i18n.search}
                            </Button>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
            <CardTable
                title={i18n.methodList}
                rowsPerPageOptions={nbPerPageLabel}
                color='bannerColor'
                rows={colorData}
                headers={ headers }
                onClickRow={method => dispatch(push(`/referencial/method/${method.id}/dashboard`))}
            />
        </Stack>
    )
}

export default MethodsApp
